import { AutoCompleteDropdown } from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import Box from "@mui/material/Box";
import {
  selectEcosystemList,
  selectEnabledEcosystems,
  selectEntityChainList,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";

import { IconButton } from "@metacrm/metacrm-material-ui/dist/Button";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import { InputField, SIZE } from "@metacrm/metacrm-material-ui/dist/InputField";
import { isAddress, getChainList } from "features/helpers/utils";
import { get, map, filter } from "lodash-es";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { useTheme } from "@mui/material/styles";
import { StyledFieldTitle } from "features/metadesk/campaign/campaignAnalysis/common.styles";
import { StyledContractFieldContainer } from "./ContractField.styles";

const ContractField = forwardRef(
  (
    {
      onDelete,
      contractData,
      contractIndex,
      latestObjectives = [],
      formData = { contracts: [] },
      setFormData = () => {},
      readonly,
      isTargetEvent,
      indexNumber,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const [entityName] = useOutletContext();
    const { contracts } = formData;

    const ecosystemList = useSelector(selectEcosystemList);
    const enabledEcosystems = useSelector(selectEnabledEcosystems);
    const selectedEcosystem = contractData.ecosystem;
    const chainList = selectedEcosystem ? getChainList(selectedEcosystem) : [];

    const chainListForDropdown = filter(
      chainList,
      (o) => !o.disabledMarketing
    ).map((chain) => ({
      label: chain.name,
      value: chain.value,
      name: chain.name,
      imgURL: chain.logo_url,
    }));
    const ecosystemListForDropdown = enabledEcosystems
      .filter((eco) => eco !== "aptos")
      .map((eco) => {
        const item = ecosystemList.find((e) => e.value === eco);
        return item
          ? {
              label: item.name,
              value: item.value,
              name: item.name,
              imgURL: item.logo_url,
            }
          : null;
      })
      .filter(Boolean);

    const theme = useTheme();

    const {
      control,
      formState: { errors: errorsInfo, isValid },
    } = useFormContext();

    const { append, replace } = useFieldArray({
      control: control,
      name: "contracts",
    });

    const replaceById = (fieldName, value) => {
      setFormData((prevForm) => ({
        ...prevForm,
        contracts: prevForm.contracts.map((item) =>
          item._id === contractData._id
            ? {
                ...item,
                [fieldName]: value,
                ...(fieldName === "ecosystem"
                  ? {
                      chainId: "",
                      smartContractAddress: "",
                    }
                  : {}),
              }
            : item
        ),
      }));
    };

    useEffect(() => {
      replace(formData.contracts);
    }, [formData.contracts]);

    // fieldName是用來control在step的value用的,而name是用來給react-hook-form用的
    const handleChange = ({ event, fieldName }) => {
      const { value } = event.target;
      replaceById(fieldName, value);
    };

    const replaceObjectById = (array, targetId, newObject) => {
      // 淺拷貝
      const newArray = array.slice();
      const index = newArray.findIndex((obj) => obj._id === targetId);
      if (index !== -1) {
        newArray[index] = newObject;
      }
      return newArray;
    };

    return (
      <StyledContractFieldContainer>
        {contracts.length > 1 && (
          <Box position="absolute" top="14px" right="19px">
            <IconButton onClick={() => onDelete(contractData._id)} size={20}>
              <i className="meta-crm-icon-ic_trash font-size-20" />
            </IconButton>
          </Box>
        )}

        <Box mb={2}>
          <InputField
            maxLength={20}
            disabled={readonly}
            control={control}
            title={formatMessage({ id: "global.labelName.name" })}
            isRequired
            size={SIZE.LARGE}
            onChange={(event) => handleChange({ event, fieldName: "name" })}
            name={`contracts[${contractIndex}].name`}
            value={contractData.name}
            errors={get(
              errorsInfo,
              `contracts.[${contractIndex}].name.message`
            )}
            rules={{
              required: { value: true, message: "Name is required." },
              minLength: {
                value: 3,
                message: formatMessage(
                  { id: "global.error.lengthInterval" },
                  { min: 3, max: 4 }
                ),
              },
            }}
            placeholder={formatMessage({ id: "global.placeholder.name" })}
          />
        </Box>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={6}>
            <StyledFieldTitle isRequired>
              {formatMessage({ id: "global.commonWord.ecosystem" })}
            </StyledFieldTitle>
            <AutoCompleteDropdown
              disabled={readonly}
              placeholder={formatMessage({ id: "menu.placeholder.select" })}
              value={ecosystemListForDropdown.find(
                (option) => option.value === contractData.ecosystem
              )}
              options={ecosystemListForDropdown}
              onChange={(event, v) => {
                handleChange({
                  event: { target: { value: v?.value } },
                  fieldName: "ecosystem",
                });
              }}
              control={control}
              name={`contracts[${contractIndex}].ecosystem`}
              errors={get(
                errorsInfo,
                `contracts.[${contractIndex}].ecosystem.message`
              )}
              size={SIZE.LARGE}
              rules={{
                required: {
                  value: true,
                  message: formatMessage({
                    id: "global.error.ecosystemRequired",
                  }),
                },
              }}
              withIcon
            />
          </Grid>
          <Grid item xs={6}>
            <Box position="relative">
              <StyledFieldTitle isRequired>
                {formatMessage({
                  id: "campaignBuild.thirdStep.field.chain.title",
                })}
              </StyledFieldTitle>
              <AutoCompleteDropdown
                disabled={readonly}
                placeholder={formatMessage({ id: "menu.placeholder.select" })}
                value={chainListForDropdown.find(
                  (option) => option.value === contractData.chainId
                )}
                options={chainListForDropdown}
                onChange={(event, v) => {
                  handleChange({
                    event: { target: { value: v?.value } },
                    fieldName: "chainId",
                  });
                }}
                control={control}
                name={`contracts[${contractIndex}].chainId`}
                errors={get(
                  errorsInfo,
                  `contracts.[${contractIndex}].chainId.message`
                )}
                size={SIZE.LARGE}
                rules={{
                  required: {
                    value: true,
                    message: formatMessage({
                      id: "campaignBuild.thirdStep.field.chain.error.required",
                    }),
                  },
                }}
                withIcon
              />
            </Box>
          </Grid>
        </Grid>

        <InputField
          disabled={readonly}
          control={control}
          title={formatMessage({
            id:
              contractData.ecosystem !== "solana"
                ? "campaignBuild.thirdStep.field.contractAddress.title"
                : "campaignBuild.thirdStep.field.programID.title",
          })}
          isRequired
          size={SIZE.LARGE}
          onChange={(event) =>
            handleChange({ event, fieldName: "smartContractAddress" })
          }
          name={`contracts[${contractIndex}].smartContractAddress`}
          value={contractData.smartContractAddress}
          errors={get(
            errorsInfo,
            `contracts.[${contractIndex}].smartContractAddress.message`
          )}
          placeholder={formatMessage({
            id: "global.placeholder.contractAddress",
          })}
          rules={{
            required: {
              value: true,
              message: formatMessage({
                id: "campaignBuild.thirdStep.field.contractAddress.error.required",
              }),
            },
            validate: (value) =>
              isAddress(value) ||
              formatMessage({
                id: "campaignBuild.thirdStep.field.contractAddress.error.notValid",
              }),
          }}
        />
      </StyledContractFieldContainer>
    );
  }
);
export default ContractField;
